/** Consensual custom photo requests — clothed, non-explicit, platform-enforced boundaries. */

export const PHOTO_REQUEST_TAGLINE = 'Request a photo. Set the terms. Stay in control.'

export const PHOTO_REQUEST_POLICY_SUMMARY =
  'Members may optionally accept paid photo requests from matches. Every request must fit the recipient’s stated boundaries, and they can decline or counter without penalty.'

export type PhotoRequestCategory =
  | 'selfie'
  | 'outfit'
  | 'fitness'
  | 'hobby'
  | 'cosplay'
  | 'date_night'
  | 'personalized_sign'
  | 'other'

export type PhotoRequestWhoCan = 'matches_only' | 'verified_only' | 'selected'

export type PhotoRequestStatus =
  | 'blocked'
  | 'held'
  | 'countered'
  | 'accepted'
  | 'delivered'
  | 'completed'
  | 'declined'
  | 'expired'
  | 'disputed'
  | 'refunded'

export type PhotoRequestDisputeReason = 'no_delivery' | 'wrong_person' | 'not_fulfilled'

export type PhotoRequestSettings = {
  enabled: boolean
  whoCanRequest: PhotoRequestWhoCan
  allowedCategories: PhotoRequestCategory[]
  excludedTerms: string[]
  minAmountCredits: number
  faceRequired: boolean
  reuseRights: 'private_view'
  requestExpiryHours: 24 | 48
}

export const PHOTO_REQUEST_CATEGORY_OPTIONS: Array<{ value: PhotoRequestCategory; label: string; hint: string }> = [
  { value: 'selfie', label: 'Selfie', hint: 'Face visible, everyday look' },
  { value: 'outfit', label: 'Outfit', hint: 'Full outfit of the day' },
  { value: 'fitness', label: 'Fitness', hint: 'Workout or activewear (clothed)' },
  { value: 'hobby', label: 'Hobby', hint: 'You with a hobby or project' },
  { value: 'cosplay', label: 'Cosplay', hint: 'Costume or character look (non-explicit)' },
  { value: 'date_night', label: 'Date-night look', hint: 'Dressed up, going-out style' },
  { value: 'personalized_sign', label: 'Personalized sign', hint: 'Handwritten sign with your name' },
  { value: 'other', label: 'Other', hint: 'Must still fit allowed boundaries' },
]

export const PHOTO_REQUEST_WHO_OPTIONS: Array<{ value: PhotoRequestWhoCan; label: string }> = [
  { value: 'matches_only', label: 'Matches only' },
  { value: 'verified_only', label: 'Verified matches only' },
  { value: 'selected', label: 'Selected people only' },
]

export const PHOTO_REQUEST_DISPUTE_OPTIONS: Array<{ value: PhotoRequestDisputeReason; label: string }> = [
  { value: 'no_delivery', label: 'Never delivered before expiry' },
  { value: 'wrong_person', label: 'Wrong person or not them in the photo' },
  { value: 'not_fulfilled', label: 'Request clearly not fulfilled (wrong category)' },
]

export const DEFAULT_PHOTO_REQUEST_SETTINGS: PhotoRequestSettings = {
  enabled: false,
  whoCanRequest: 'matches_only',
  allowedCategories: ['selfie', 'outfit'],
  excludedTerms: [],
  minAmountCredits: 25,
  faceRequired: true,
  reuseRights: 'private_view',
  requestExpiryHours: 48,
}

export const PHOTO_REQUEST_MIN_CREDITS = 10
export const PHOTO_REQUEST_MAX_CREDITS = 500
export const PHOTO_REQUEST_REVIEW_HOURS = 24

/** Categories blocked platform-wide regardless of user prefs (v1: non-explicit only). */
export const PLATFORM_BLOCKED_CATEGORY_HINTS: Record<string, string> = {
  nude: 'Nudity and explicit content are not allowed on PairVelle.',
  lingerie: 'Lingerie and sexually explicit requests are not allowed.',
  underwear: 'Underwear-focused requests are not allowed.',
  sexual: 'Sexual services or gratification requests are not allowed.',
  meetup: 'Payment tied to meeting or sexual activity is not allowed.',
  escort: 'Escorting or compensated dating is not allowed.',
  prostitution: 'Prostitution or paid sexual activity is not allowed.',
}

const ABSOLUTE_BAN_PATTERNS: Array<{ re: RegExp; code: string; userMessage: string; safety?: boolean }> = [
  { re: /\b(send|show|give)\b.{0,30}\b(nude|nudes|naked|nsfw|explicit|onlyfans)\b/i, code: 'explicit', userMessage: PLATFORM_BLOCKED_CATEGORY_HINTS.nude },
  { re: /\b(lingerie|underwear|thong|bra\s*and\s*panties|panties only)\b/i, code: 'lingerie', userMessage: PLATFORM_BLOCKED_CATEGORY_HINTS.lingerie },
  { re: /\b(blowjob|handjob|sex tape|porn|xxx|dick pic|boob pic)\b/i, code: 'sexual', userMessage: PLATFORM_BLOCKED_CATEGORY_HINTS.sexual },
  { re: /\b(escort|prostitut|pay for sex|sex for money|happy ending)\b/i, code: 'escort', userMessage: PLATFORM_BLOCKED_CATEGORY_HINTS.escort },
  { re: /\b(venmo|paypal|zelle|cashapp|cash app|bitcoin|crypto)\b/i, code: 'off_platform', userMessage: 'Payments must stay on PairVelle — off-platform payment is not allowed.' },
  { re: /\b(pay\s*(me\s*)?(on|via|through)\s*(cash\s*app|venmo|paypal|zelle|crypto|bitcoin|cashapp))\b/i, code: 'off_platform', userMessage: 'Payments must stay on PairVelle — off-platform payment is not allowed.' },
  { re: /\b(meet\s*(up|me)|come\s*over|hotel|your place)\b.{0,40}\b(pay|paid|payment|\$)\b/i, code: 'meetup', userMessage: PLATFORM_BLOCKED_CATEGORY_HINTS.meetup },
  { re: /\b(minor|under\s*18|child|teen\b(?!\s*age)|schoolgirl)\b/i, code: 'minor', userMessage: 'Requests involving minors are never allowed.', safety: true },
  { re: /\b(deepfake|face\s*swap|ai\s*nude)\b/i, code: 'deepfake', userMessage: 'Impersonation and sexual deepfakes are not allowed.', safety: true },
  { re: /\b(ssn|social security|driver'?s?\s*license|passport|credit card)\b/i, code: 'doxxing', userMessage: 'IDs, financial data, and doxxing material are not allowed.', safety: true },
  { re: /\b(license plate|workplace address|home address)\b/i, code: 'doxxing', userMessage: 'Addresses, workplaces, and doxxing material are not allowed.' },
]

const BLACKMAIL_PATTERNS: RegExp[] = [
  /\b(or\s*else|if\s*you\s*don'?t|unless\s*you)\b/i,
  /\b(i'?ll\s*(tell|expose|leak|post|share))\b/i,
  /\b(blackmail|extort)\b/i,
  /\b(send\s*this\s*or\s*(i|we)\b)/i,
  /\b(ruin\s*your|destroy\s*your)\b.{0,30}\b(life|reputation|career)\b/i,
]

const CATEGORY_KEYWORD_BLOCKS: Partial<Record<PhotoRequestCategory, RegExp[]>> = {
  selfie: [/\b(lingerie|topless|nude)\b/i],
  outfit: [/\b(lingerie|underwear|see[- ]?through)\b/i],
  fitness: [/\b(shirtless|nude workout|naked)\b/i],
  other: [/\b(nude|nsfw|explicit|lingerie|underwear)\b/i],
}

export type PhotoRequestValidationResult =
  | { ok: true }
  | { ok: false; code: string; message: string; safetyFlag?: 'blackmail' | 'coercion' | 'minor' | 'doxxing' }

export function parsePhotoRequestSettings(row: {
  enabled?: number | null
  who_can_request?: string | null
  allowed_categories?: string | null
  excluded_terms?: string | null
  min_amount_credits?: number | null
  face_required?: number | null
  reuse_rights?: string | null
  request_expiry_hours?: number | null
} | null): PhotoRequestSettings {
  if (!row) return { ...DEFAULT_PHOTO_REQUEST_SETTINGS }
  let allowedCategories: PhotoRequestCategory[] = DEFAULT_PHOTO_REQUEST_SETTINGS.allowedCategories
  let excludedTerms: string[] = []
  try {
    const parsed = JSON.parse(row.allowed_categories ?? '[]') as unknown
    if (Array.isArray(parsed) && parsed.length) {
      allowedCategories = parsed.filter((v): v is PhotoRequestCategory => typeof v === 'string')
    }
  } catch {
    /* keep default */
  }
  try {
    const parsed = JSON.parse(row.excluded_terms ?? '[]') as unknown
    if (Array.isArray(parsed)) excludedTerms = parsed.filter((v): v is string => typeof v === 'string').slice(0, 20)
  } catch {
    /* keep default */
  }
  const expiry = row.request_expiry_hours === 24 ? 24 : 48
  return {
    enabled: !!row.enabled,
    whoCanRequest: (row.who_can_request as PhotoRequestWhoCan) ?? 'matches_only',
    allowedCategories,
    excludedTerms,
    minAmountCredits: Math.max(PHOTO_REQUEST_MIN_CREDITS, row.min_amount_credits ?? DEFAULT_PHOTO_REQUEST_SETTINGS.minAmountCredits),
    faceRequired: row.face_required !== 0,
    reuseRights: 'private_view',
    requestExpiryHours: expiry,
  }
}

export function serializePhotoRequestSettings(settings: PhotoRequestSettings): Record<string, unknown> {
  return {
    enabled: settings.enabled ? 1 : 0,
    who_can_request: settings.whoCanRequest,
    allowed_categories: JSON.stringify(settings.allowedCategories),
    excluded_terms: JSON.stringify(settings.excludedTerms.map((t) => t.trim().slice(0, 64)).filter(Boolean)),
    min_amount_credits: settings.minAmountCredits,
    face_required: settings.faceRequired ? 1 : 0,
    reuse_rights: 'private_view',
    request_expiry_hours: settings.requestExpiryHours,
  }
}

function normalizeText(parts: string[]): string {
  return parts.join(' ').trim().toLowerCase()
}

/** Platform-wide + creator boundary enforcement before a request is sent. */
export function validatePhotoRequestInput(opts: {
  category: PhotoRequestCategory
  promptNotes?: string | null
  amountCredits: number
  creatorSettings: PhotoRequestSettings
}): PhotoRequestValidationResult {
  const { category, promptNotes, amountCredits, creatorSettings } = opts
  const blob = normalizeText([category, promptNotes ?? ''])

  for (const pattern of BLACKMAIL_PATTERNS) {
    if (pattern.test(blob)) {
      return {
        ok: false,
        code: 'blackmail',
        message: 'This request was blocked for safety. Coercion and blackmail are never allowed.',
        safetyFlag: 'blackmail',
      }
    }
  }

  for (const rule of ABSOLUTE_BAN_PATTERNS) {
    if (rule.re.test(blob)) {
      return {
        ok: false,
        code: rule.code,
        message: rule.userMessage,
        safetyFlag: rule.safety ? (rule.code as PhotoRequestValidationResult extends { safetyFlag?: infer S } ? S : never) : undefined,
      }
    }
  }

  if (!creatorSettings.enabled) {
    return { ok: false, code: 'disabled', message: 'This member is not accepting photo requests right now.' }
  }

  if (!creatorSettings.allowedCategories.includes(category)) {
    return {
      ok: false,
      code: 'category_not_allowed',
      message: 'That category is not in their allowed list. Choose one of their enabled categories.',
    }
  }

  for (const term of creatorSettings.excludedTerms) {
    const t = term.trim().toLowerCase()
    if (t && blob.includes(t)) {
      return {
        ok: false,
        code: 'excluded_term',
        message: `This request includes something they marked as off-limits (“${term.slice(0, 40)}”).`,
      }
    }
  }

  const catBlocks = CATEGORY_KEYWORD_BLOCKS[category] ?? []
  for (const re of catBlocks) {
    if (re.test(blob)) {
      return { ok: false, code: 'category_boundary', message: 'That wording is not allowed for this category.' }
    }
  }

  if (amountCredits < creatorSettings.minAmountCredits) {
    return {
      ok: false,
      code: 'below_minimum',
      message: `Minimum for this member is ${creatorSettings.minAmountCredits} credits.`,
    }
  }

  if (amountCredits < PHOTO_REQUEST_MIN_CREDITS || amountCredits > PHOTO_REQUEST_MAX_CREDITS) {
    return {
      ok: false,
      code: 'amount_bounds',
      message: `Amount must be between ${PHOTO_REQUEST_MIN_CREDITS} and ${PHOTO_REQUEST_MAX_CREDITS} credits.`,
    }
  }

  return { ok: true }
}

export function categoryLabel(category: PhotoRequestCategory): string {
  return PHOTO_REQUEST_CATEGORY_OPTIONS.find((o) => o.value === category)?.label ?? category
}

export function statusLabel(status: PhotoRequestStatus): string {
  const map: Record<PhotoRequestStatus, string> = {
    blocked: 'Blocked',
    held: 'Awaiting response',
    countered: 'Counter offer',
    accepted: 'Accepted — awaiting photo',
    delivered: 'Delivered — review window',
    completed: 'Completed',
    declined: 'Declined',
    expired: 'Expired',
    disputed: 'Under review',
    refunded: 'Refunded',
  }
  return map[status] ?? status
}
